import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { Badge } from '@/components/ui/Badge';
import { ApiError } from '@/lib/api/client';
import { useBillingSettings } from '@/hooks/useBillingSettings';
import SafetyAlertBanner from '@/components/SafetyAlertBanner';
import { ScheduleBadge } from './ScheduleBadge';
import { SubstituteFlow } from './SubstitutePicker';
import { DeclineDialog, ItemStatusBadge, PrescriptionStatusBadge, reasonLabel } from './DeclineDialog';
import { undoPrescriptionDecline } from './api';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { keys } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { useInventory } from './useInventory';
import { useActivePrescriptions } from './useActivePrescriptions';
import { usePrescriptionDetail } from './usePrescriptionDetail';
import { useCreatePharmacyBill } from './useCreatePharmacyBill';
import { useDispensingSafetyCheck } from './useDispensingSafetyCheck';
import { useDispensingSettings } from './useNearExpiry';
import { NearExpiryBadge, LaterBatchPanel, batchSelectionSummary } from './BatchSelectionPanel';
import { pickFefoBatch, unresolvedLaterBatches, applyBatchSelectionReason, isNearExpiry } from './batchSelection';
import {
  cartTotals, needsPrescriberFor, resolveGstPercent, reconcileGstOptions,
  unresolvedQuantityOverages as findUnresolvedQuantityOverages,
  unresolvedOffPrescription as findUnresolvedOffPrescription,
  offPrescriptionMayBeMissedSubstitution,
  applyReasonToLines,
} from './dispensingCart';
import type { InventoryItem, DispensablePrescription, DispensablePrescriptionItem, PharmacyBillResult } from './types';

const isExpired = (d: string | null | undefined) => !!d && new Date(d) < new Date();

// Mirrors backend/utils/ipSafetyRules.js's normaliseDrug — lowercase, strip
// punctuation, collapse whitespace — so the client-side match below agrees
// with what the server would compute for the same names.
const normalizeDrugName = (s: string | null | undefined) =>
  (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

interface CartItem {
  inventory_id: number;
  medicine_id: number;
  name: string;
  qty: number;
  mrp: number;
  gst: number;
  schedule: InventoryItem['schedule'];
  batch: string;
  expiry: string;
  available: number;
  /** Set when this line fulfils a specific prescription line — see
   * migration_dispensing_link.sql. Rendered as an "Rx" badge in the cart. */
  prescription_item_id?: number;
  /** Required by the backend when this line's drug doesn't match what was
   * prescribed on prescription_item_id — otherwise the sale is hard-blocked.
   * See migration_dispensing_substitution.sql. */
  substitution_reason?: string;
  /** Required by the backend when this line's quantity, cumulative with
   * everything already dispensed against prescription_item_id, exceeds what
   * remains on the prescription — otherwise the sale is hard-blocked. See
   * migration_dispensing_quantity_override.sql. */
  quantity_override_reason?: string;
  /** Required by the backend when this line has no prescription_item_id at
   * all — added via the free "Add medicine" picker rather than resolved from
   * a prescribed line. See migration_dispensing_off_prescription_reason.sql. */
  off_prescription_reason?: string;
  /** Required by the backend when this line sells a later-expiry batch while
   * an earlier one could cover it — see batchSelection.ts. */
  batch_selection_reason?: string;
  batch_selection_remarks?: string;
}

export default function DispensingTab() {
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const { near_expiry_days: nearExpiryDays, batch_selection_reasons: batchReasons } = useDispensingSettings();
  const [selConsult, setSelConsult] = useState<DispensablePrescription | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [payMode, setPayMode] = useState<'Cash' | 'UPI' | 'Card'>('Cash');
  const [payStatus, setPayStatus] = useState<'Paid' | 'Pending'>('Paid');
  const [prescriberName, setPrescriberName] = useState('');
  const [prescriberReg, setPrescriberReg] = useState('');
  const [advancedBatch, setAdvancedBatch] = useState(false);
  const [prescriberError, setPrescriberError] = useState('');
  const [confirmedBill, setConfirmedBill] = useState<PharmacyBillResult | null>(null);
  /** id of the prescription_item currently showing its inline substitute picker, if any. */
  const [substitutingFor, setSubstitutingFor] = useState<number | null>(null);
  /** Shared free-text reason applied to every currently-over-quantity cart
   * line at once when "Apply" is clicked — see unresolvedQuantityOverages
   * below. One shared field rather than one per line, matching how the
   * Schedule H1/X prescriber name above already applies once to the whole
   * cart rather than once per H1/X line. */
  const [quantityOverrideReason, setQuantityOverrideReason] = useState('');
  /** Shared free-text reason applied to every currently-unlinked cart line at
   * once when "Apply" is clicked — same one-shared-field pattern as
   * quantityOverrideReason above. */
  const [offPrescriptionReason, setOffPrescriptionReason] = useState('');

  const inventoryQuery = useInventory({ channel: 'visit' });
  const prescriptionsQuery = useActivePrescriptions();
  const prescriptionDetailQuery = usePrescriptionDetail(selConsult?.id ?? null);
  // G9: "Patient declined the rest" on a line, and undoing it.
  const [declining, setDeclining] = useState<DispensablePrescriptionItem | null>(null);
  const [undoing, setUndoing] = useState<number | null>(null);
  const qc = useQueryClient();
  const undo = useMutation({
    mutationFn: (id: number) => undoPrescriptionDecline(id),
    onSuccess: () => {
      toast({ title: 'Decline undone — the line is open again', variant: 'success' });
      if (selConsult) qc.invalidateQueries({ queryKey: keys.prescriptions.detail(selConsult.id) });
    },
    onError: (e) => toast({ title: 'Couldn’t undo', description: e instanceof Error ? e.message : String(e), variant: 'danger' }),
  });
  const createBill = useCreatePharmacyBill();

  const inventory = inventoryQuery.data ?? [];
  const prescriptions = prescriptionsQuery.data ?? [];
  const prescriptionItems = prescriptionDetailQuery.data?.items ?? [];
  const loading = inventoryQuery.isLoading || prescriptionsQuery.isLoading;

  const { subtotal, gstAmt, total } = cartTotals(items, gstEnabled);
  const needsPrescriber = needsPrescriberFor(items);

  // FEFO: one entry per distinct medicine, defaulting to the batch expiring
  // soonest — replaces free batch-picking so staff cannot accidentally sell
  // a later batch while an earlier one still has stock. Adding from here (or
  // a prescribed line) re-resolves to the earliest batch that covers the
  // whole quantity — see addItem's `fefo` flag and batchSelection.ts.
  const dispensable = useMemo(
    () => inventory.filter((m) => m.quantity_available > 0 && !isExpired(m.expiry_date)),
    [inventory]
  );
  const fefoGroups = useMemo(() => {
    const groups: Record<number, InventoryItem> = {};
    dispensable.forEach((m) => {
      const key = m.medicine_id;
      if (!groups[key] || new Date(m.expiry_date) < new Date(groups[key]!.expiry_date)) groups[key] = m;
    });
    return groups;
  }, [dispensable]);
  const totalsByMed = useMemo(() => {
    const totals: Record<number, number> = {};
    dispensable.forEach((m) => { totals[m.medicine_id] = (totals[m.medicine_id] || 0) + m.quantity_available; });
    return totals;
  }, [dispensable]);

  // Best-effort match between a manually-picked medicine and a not-yet-
  // fulfilled prescribed line, mirroring backend/utils/rxSafetyChecks.js's
  // matchesPrescribedDrug (medicine_id first, else normalized name substring
  // match either direction). Needed because a prescribed line's medicine_id
  // is null whenever the doctor typed the drug as free text rather than
  // picking it from the master (PrescriptionBuilder.tsx allows both) — the
  // FEFO auto-resolve (fefoGroups[it.medicine_id]) then can't find a batch
  // for it even when the exact drug IS in stock, showing "not in stock —
  // substitute" for a line that's actually just unresolved. Without this,
  // picking that same drug from the free "Add medicine" list would add an
  // unlinked line and wrongly trip the off-prescription gate below for a
  // drug the doctor did prescribe.
  const findMatchingPrescriptionItem = (med: InventoryItem): DispensablePrescriptionItem | undefined => {
    const dispensedNames = [normalizeDrugName(med.generic_name), normalizeDrugName(med.brand_name)].filter(Boolean);
    return prescriptionItems.find((it) => {
      if (it.remaining_quantity === 0) return false;
      if (items.some((cartItem) => cartItem.prescription_item_id === it.id)) return false;
      if (it.medicine_id != null && it.medicine_id === med.medicine_id) return true;
      const prescribedNames = [normalizeDrugName(it.drug_name), normalizeDrugName(it.brand_name)].filter(Boolean);
      return prescribedNames.some((p) => p.length >= 4 && dispensedNames.some((d) => d.includes(p) || p.includes(d)));
    });
  };

  // `fefo` — the caller picked a MEDICINE (the FEFO dropdown, a prescribed
  // line), not a specific batch, so the batch is re-resolved here to the
  // earliest-expiring one that covers the whole quantity (batchSelection.ts).
  // The advanced batch picker and the Substitute flow pass a batch the
  // pharmacist chose on purpose and leave it alone.
  const addItem = (invId: string, prescriptionItemId?: number, qtyOverride?: number, substitutionReason?: string, fefo = false) => {
    const picked = inventory.find((m) => m.id === parseInt(invId, 10));
    if (!picked) return;
    const autoMatch = prescriptionItemId == null ? findMatchingPrescriptionItem(picked) : undefined;
    const resolvedPrescriptionItemId = prescriptionItemId ?? autoMatch?.id;
    const defaultQty = qtyOverride ?? (autoMatch ? autoMatch.remaining_quantity ?? 1 : 1);
    const med = fefo
      ? pickFefoBatch(dispensable.filter((m) => m.medicine_id === picked.medicine_id), defaultQty, items) ?? picked
      : picked;
    const validGst = resolveGstPercent(parseFloat(med.gst_percent) || 12, gstPercentageOptions);
    setItems((prev) => [
      ...prev,
      {
        inventory_id: med.id,
        medicine_id: med.medicine_id,
        name: `${med.generic_name} ${med.strength || ''}`.trim(),
        qty: Math.min(defaultQty, med.quantity_available) || 1,
        mrp: parseFloat(med.mrp),
        gst: gstEnabled ? validGst : 0,
        schedule: med.schedule,
        batch: med.batch_number,
        expiry: med.expiry_date,
        available: med.quantity_available,
        prescription_item_id: resolvedPrescriptionItemId,
        substitution_reason: substitutionReason,
      },
    ]);
  };

  // Prescription lines already added to the cart, keyed by prescription_item_id —
  // so "Add to cart" for a prescribed line can be disabled once it's there,
  // rather than letting the pharmacist queue the same line twice by accident.
  const cartedPrescriptionItemIds = useMemo(
    () => new Set(items.map((i) => i.prescription_item_id).filter((id): id is number => id != null)),
    [items]
  );

  // Adds a prescribed line to the cart, auto-resolving it to the earliest-
  // expiry batch for that medicine (same FEFO source the manual dropdown
  // below uses) and defaulting quantity to what's still owed. Not every
  // prescribed drug is guaranteed to exist in this exact inventory master —
  // when it doesn't resolve, the row below says so and the pharmacist falls
  // back to the manual "Add medicine" flow (which can't carry a
  // prescription_item_id, since it was never matched to one).
  const addPrescriptionItem = (item: DispensablePrescriptionItem) => {
    const batch = item.medicine_id != null ? fefoGroups[item.medicine_id] : undefined;
    if (!batch) return;
    const remaining = item.remaining_quantity ?? 1;
    addItem(String(batch.id), item.id, remaining, undefined, true);
  };

  // Pre-filled from the medicine's own master gst_percent, but staff can
  // override per sale — e.g. a discount/exempt scheme for one dispense
  // shouldn't require editing the product's permanent tax classification.
  const updateItemGst = (idx: number, value: string) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, gst: parseFloat(value) || 0 } : it)));

  // If the admin removes a percentage currently selected on an item already
  // in the cart, fall back to the first configured option.
  useEffect(() => {
    setItems((prev) => reconcileGstOptions(prev, gstPercentageOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercentageOptions]);

  // Live check against the cart while it's being built, before checkout —
  // debounced the same way PrescriptionBuilder.tsx's own live check is.
  // Two different things ride on this one call: allergy/duplicate-therapy
  // warnings (ADVISORY ONLY, never wired into "Generate pharmacy bill"'s
  // disabled prop) and identity mismatches (a line linked to a prescribed
  // item that dispenses a different drug — the backend HARD-BLOCKS these
  // unless a substitution_reason is attached, which the Substitute picker
  // above already captures at add-time; this is a defensive second check,
  // e.g. if something about the linked item changed after it was added).
  // Keyed on substitution_reason too (unlike PharmacyStage's own use of this
  // hook), so a substitution the pharmacist just explained re-runs the check
  // rather than continuing to show a resolved mismatch.
  const { alerts: safetyAlerts, mismatches } = useDispensingSafetyCheck(
    selConsult?.patient_id,
    items,
    items.map((i) => `${i.inventory_id}:${i.prescription_item_id ?? ''}:${i.substitution_reason ?? ''}`).join(',')
  );

  // The check endpoint doesn't know which lines already carry a
  // substitution_reason (it isn't sent to /pharmacy/check, only to
  // /pharmacy/create) — filter those out here so a substitution the
  // pharmacist already explained doesn't keep showing as unresolved.
  const unresolvedMismatches = useMemo(
    () => mismatches.filter((m) => !items.some((i) => i.inventory_id === m.inventory_id && i.substitution_reason)),
    [mismatches, items]
  );

  // remaining_quantity is already computed server-side (per prescription
  // item, across every prior bill — see prescriptionDispenseStatus.js) and
  // came down with prescriptionItems, so no extra round trip is needed to
  // flag a cart line that now asks for more than what's left.
  const remainingByPrescriptionItemId = useMemo(
    () => new Map(prescriptionItems.map((it) => [it.id, it.remaining_quantity])),
    [prescriptionItems]
  );
  // A cart line linked to a prescription item whose quantity exceeds what
  // remains on that line — the quantity counterpart to unresolvedMismatches
  // above. See dispensingCart.ts's own header for why this rule is shared
  // with PharmacyStage.tsx but the mismatch machinery above is not.
  const unresolvedQuantityOverages = useMemo(
    () => findUnresolvedQuantityOverages(items, remainingByPrescriptionItemId),
    [items, remainingByPrescriptionItemId]
  );

  const applyQuantityOverrideReason = () => {
    const reason = quantityOverrideReason.trim();
    if (!reason) return;
    const overIds = new Set(unresolvedQuantityOverages.map((i) => i.inventory_id));
    setItems((prev) => applyReasonToLines(prev, overIds, 'quantity_override_reason', reason));
    setQuantityOverrideReason('');
  };

  // Every patient on this screen has an active prescription, so a cart line
  // with no prescription_item_id at all is a medicine the doctor never
  // ordered — not a substitution of one, an addition outside the list
  // entirely. The backend hard-blocks this unless off_prescription_reason is
  // set; this is the same live, resolve-before-checkout pattern as
  // unresolvedQuantityOverages above.
  const unresolvedOffPrescription = useMemo(() => findUnresolvedOffPrescription(items), [items]);

  // Advisory nudge, not a gate — see dispensingCart.ts's own header on why
  // this can happen (free-add name-matching missed a genuine substitution)
  // and why it's never blocking (off-prescription is a legitimate path too).
  const offPrescriptionMayBeSubstitution = useMemo(
    () => offPrescriptionMayBeMissedSubstitution(items, prescriptionItems),
    [items, prescriptionItems]
  );

  const applyOffPrescriptionReason = () => {
    const reason = offPrescriptionReason.trim();
    if (!reason) return;
    const unlinkedIds = new Set(unresolvedOffPrescription.map((i) => i.inventory_id));
    setItems((prev) => applyReasonToLines(prev, unlinkedIds, 'off_prescription_reason', reason));
    setOffPrescriptionReason('');
  };

  // A line on a later-expiry batch while an earlier one could cover it —
  // picked via the advanced batch list or the Substitute flow. The backend
  // hard-blocks these unless batch_selection_reason is set; same live,
  // resolve-before-checkout pattern as the two banners above.
  const unresolvedLater = useMemo(() => unresolvedLaterBatches(items, dispensable), [items, dispensable]);

  const applyLaterBatchReason = (ids: Set<number>, reason: string, remarks: string) =>
    setItems((prev) => applyBatchSelectionReason(prev, ids, reason, remarks));

  // "Use batch X" on the banner: move the line onto the earlier batch rather
  // than explaining the later one. Everything else about the line stays.
  const switchToEarlierBatch = (line: CartItem, earlierId: number) => {
    const earlier = inventory.find((m) => m.id === earlierId);
    if (!earlier) return;
    setItems((prev) => prev.map((it) => (it === line
      ? { ...it, inventory_id: earlier.id, batch: earlier.batch_number, expiry: earlier.expiry_date, available: earlier.quantity_available, mrp: parseFloat(earlier.mrp) }
      : it)));
  };

  const selectPrescription = (c: DispensablePrescription) => {
    setSelConsult(c);
    setItems([]);
    setPrescriberName('');
    setPrescriberReg('');
    setConfirmedBill(null);
    setSubstitutingFor(null);
    setQuantityOverrideReason('');
    setOffPrescriptionReason('');
  };

  const genBill = () => {
    if (!selConsult || items.length === 0) return;
    if (needsPrescriber && !prescriberName.trim()) {
      setPrescriberError('Prescriber name is required — this bill includes a Schedule H1/X medicine.');
      return;
    }
    if (unresolvedMismatches.length) {
      setPrescriberError('One or more items don’t match what was prescribed — add a substitution reason before generating the bill.');
      return;
    }
    if (unresolvedQuantityOverages.length) {
      setPrescriberError('One or more items exceed the prescribed quantity — add a reason before generating the bill.');
      return;
    }
    if (unresolvedOffPrescription.length) {
      setPrescriberError('One or more items aren’t on this patient’s prescription — add a reason before generating the bill.');
      return;
    }
    if (unresolvedLater.length) {
      setPrescriberError('One or more items use a later-expiry batch while an earlier one is available — add a reason or switch batch before generating the bill.');
      return;
    }
    setPrescriberError('');
    createBill.mutate(
      {
        patient_id: selConsult.patient_id,
        consultation_id: selConsult.consultation_id ?? undefined,
        visit_id: selConsult.visit_id ?? undefined,
        payment_mode: payMode,
        payment_status: payStatus,
        prescriber_name: prescriberName.trim() || undefined,
        prescriber_reg_no: prescriberReg.trim() || undefined,
        items: items.map((i) => ({
          inventory_id: i.inventory_id, name: i.name, quantity: i.qty, gst_percent: gstEnabled ? i.gst : 0,
          prescription_item_id: i.prescription_item_id, substitution_reason: i.substitution_reason,
          quantity_override_reason: i.quantity_override_reason,
          off_prescription_reason: i.off_prescription_reason,
          batch_selection_reason: i.batch_selection_reason,
          batch_selection_remarks: i.batch_selection_remarks,
        })),
      },
      {
        onSuccess: (bill) => {
          setConfirmedBill(bill);
          setItems([]);
          setSelConsult(null);
          setPrescriberName('');
          setPrescriberReg('');
        },
      }
    );
  };

  const billError = createBill.error instanceof ApiError ? createBill.error.message : null;

  return (
    <div className="grid gap-4 min-[900px]:grid-cols-2">
      <Card>
        <div className="mb-3.5 text-sm font-semibold">Active prescriptions</div>
        {(billError || prescriberError) && (
          <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{billError || prescriberError}</div>
        )}
        {confirmedBill && (
          <div className="mb-3 rounded-[var(--radius)] border border-success-soft bg-success-soft px-3.5 py-2.5 text-[13px] text-success-ink">
            ✓ Pharmacy bill {confirmedBill.bill_number} for ₹{parseFloat(confirmedBill.total_amount).toFixed(2)} generated
            {confirmedBill.payment_status === 'Pending' ? ' — marked Pending.' : '.'}
          </div>
        )}
        {loading ? (
          <LoadingState />
        ) : prescriptions.length === 0 ? (
          <EmptyState label="No active prescriptions to dispense" />
        ) : (
          prescriptions.map((c) => (
            <div
              key={c.id}
              onClick={() => selectPrescription(c)}
              className="mb-2.5 cursor-pointer rounded-[var(--radius)] border p-[11px]"
              style={{
                borderColor: selConsult?.id === c.id ? 'var(--teal)' : '#E5E7EB',
                background: selConsult?.id === c.id ? 'rgba(11,95,214,0.06)' : 'transparent',
              }}
            >
              <div className="text-[13px] font-semibold">{c.patient_name}</div>
              <div className="mt-0.5 text-[11px] text-ink-muted">
                {c.uhid} {c.prescribed_at ? `· ${new Date(c.prescribed_at).toLocaleString('en-IN')}` : ''}
              </div>
              <Badge variant="success" className="mt-1.5">
                {c.item_count} medicine{c.item_count === 1 ? '' : 's'}{c.prescriber_name ? ` · Dr. ${c.prescriber_name}` : ''}
              </Badge>
            </div>
          ))
        )}
      </Card>

      {selConsult && (
        <DeclineDialog item={declining} prescriptionId={selConsult.id} onClose={() => setDeclining(null)} />
      )}
      <ConfirmDialog
        open={undoing != null}
        onOpenChange={(o) => { if (!o) setUndoing(null); }}
        title="Undo this decline?"
        description="The units become due again, and a prescription that closed only because of this decline re-opens."
        confirmLabel="Undo decline"
        variant="primary"
        onConfirm={() => { if (undoing != null) undo.mutate(undoing); }}
      />
      {selConsult && (
        <Card>
          <div className="mb-3.5 text-sm font-semibold">Issue medicines</div>

          {prescriptionDetailQuery.isLoading ? (
            <LoadingState />
          ) : prescriptionItems.length > 0 ? (
            <div className="mb-3.5">
              <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-ink-muted">
                Prescribed by the doctor
                <PrescriptionStatusBadge status={prescriptionDetailQuery.data?.dispense_status} />
              </div>
              {prescriptionItems.map((it) => {
                const carted = cartedPrescriptionItemIds.has(it.id);
                const remaining = it.remaining_quantity;
                const fullyDispensed = remaining === 0;
                const resolvedBatch = it.medicine_id != null ? fefoGroups[it.medicine_id] : undefined;
                // A prescribed line can fail to auto-resolve a batch for two
                // different reasons, and they read very differently to a
                // pharmacist: no medicine_id at all means the doctor typed
                // the drug as free text (PrescriptionBuilder.tsx allows
                // this) rather than picking it from the master, so FEFO has
                // nothing to key off — the drug may well be in stock, as
                // findMatchingPrescriptionItem above will still catch via
                // the picker below. A medicine_id that's simply missing from
                // fefoGroups means it genuinely has none in stock.
                const unresolvedMedicine = it.medicine_id == null;
                // A null quantity is only a genuine open-ended order (SOS/STAT
                // — "as needed", "at once" — see backend/utils/
                // prescriptionQuantity.js's own carve-out) when the prescriber
                // actually marked it as such. Any OTHER null quantity means a
                // dose was simply never recorded — PrescriptionBuilder.tsx's
                // Dose quantity field is optional, nothing requires it — and
                // the dispensing gate can't derive a cap for it, so the
                // pharmacist gets no warning at all today and this line can be
                // dispensed without limit, indefinitely. Flag that case
                // specifically so it isn't silently indistinguishable from a
                // real PRN order.
                const openEndedByDesign = ['SOS', 'STAT'].includes((it.frequency_code || '').trim().toUpperCase());
                const unboundedDataGap = it.quantity == null && !openEndedByDesign;
                const substituting = substitutingFor === it.id;
                return (
                  <div key={it.id} className="mb-1.5 rounded-[var(--radius)] border border-line px-2.5 py-2">
                    <div className="flex items-center gap-2.5">
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
                          <span>
                            {it.drug_name} {it.brand_name ? `(${it.brand_name})` : ''} {it.strength || ''}
                          </span>
                          {unboundedDataGap && (
                            <Badge
                              variant="warn"
                              title="No dose was recorded on this prescription line, so there's no way to compute a total — dispensing this drug has no quantity limit until the prescription is corrected."
                            >
                              ⚠ No quantity limit recorded
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-ink-muted">
                          Prescribed {it.quantity ?? '—'} · Dispensed {it.dispensed_quantity}
                          {(it.declined_quantity ?? 0) > 0 && ` · Declined ${it.declined_quantity}`}
                          {remaining != null && ` · Remaining ${remaining}`}
                        </div>
                      </div>
                      {fullyDispensed ? (
                        it.dispense_status ? <ItemStatusBadge status={it.dispense_status} /> : <Badge variant="success">✓ Dispensed</Badge>
                      ) : carted ? (
                        <Badge variant="info">In cart</Badge>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          {resolvedBatch && <Button size="sm" onClick={() => addPrescriptionItem(it)}>+ Add to cart</Button>}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSubstitutingFor(substituting ? null : it.id)}
                          >
                            {resolvedBatch
                              ? '🔀 Substitute'
                              : unresolvedMedicine
                              ? '🔀 Not linked to inventory — pick a batch'
                              : '🔀 Not in stock — substitute'}
                          </Button>
                          {remaining != null && remaining > 0 && (
                            <Button size="sm" variant="ghost" onClick={() => setDeclining(it)}
                              title="The patient won't take the rest now — close this line without a sale">
                              Patient declined…
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {substituting && (
                      <SubstituteFlow
                        dispensable={dispensable}
                        defaultQty={remaining ?? 1}
                        unresolvedMedicine={unresolvedMedicine}
                        prescriptionItemId={it.id}
                        onCancel={() => setSubstitutingFor(null)}
                        onDispense={(invId, qty, reason) => {
                          addItem(invId, it.id, qty, reason);
                          setSubstitutingFor(null);
                        }}
                      />
                    )}
                  </div>
                );
              })}
              {(prescriptionDetailQuery.data?.declines ?? []).length > 0 && (
                <div className="mt-2 rounded-[var(--radius)] border border-line px-2.5 py-2 text-[11.5px]">
                  <div className="mb-1 font-semibold text-ink-muted">Declined</div>
                  {(prescriptionDetailQuery.data?.declines ?? []).map((d) => {
                    const line = prescriptionItems.find((x) => x.id === d.prescription_item_id);
                    return (
                      <div key={d.id} className={`flex flex-wrap items-center justify-between gap-2 py-0.5 ${d.undone_at ? 'text-ink-muted line-through' : ''}`}>
                        <span>
                          {d.quantity} × {line?.drug_name ?? 'medicine'} — {reasonLabel(d.reason)}{d.note ? ` (${d.note})` : ''} · {d.recorded_by}
                          {d.undone_at && <span className="no-underline"> · undone by {d.undone_by}</span>}
                        </span>
                        {!d.undone_at && (
                          <Button size="sm" variant="ghost" onClick={() => setUndoing(d.id)} disabled={undo.isPending}>Undo</Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          <FormField label="Add medicine (FEFO batch, earliest expiry first)">
            <NativeSelect
              defaultValue=""
              onChange={(e) => { if (e.target.value) { addItem(e.target.value, undefined, undefined, undefined, true); e.target.value = ''; } }}
            >
              <option value="">Select medicine…</option>
              {Object.values(fefoGroups).map((m) => (
                <option key={m.medicine_id} value={m.id}>
                  {isNearExpiry(m.expiry_date, nearExpiryDays) ? '⏳ ' : ''}{m.generic_name} {m.strength} {m.schedule !== 'OTC' ? `[${m.schedule}]` : ''} — batch {m.batch_number}, exp{' '}
                  {new Date(m.expiry_date).toLocaleDateString('en-IN')} ({totalsByMed[m.medicine_id]} total)
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <Button variant="ghost" size="sm" className="mb-2.5" onClick={() => setAdvancedBatch((a) => !a)}>
            {advancedBatch ? '▲ Hide' : '▼'} Advanced: pick a specific batch
          </Button>
          {advancedBatch && (
            <div className="mb-3.5">
              <NativeSelect defaultValue="" onChange={(e) => { if (e.target.value) { addItem(e.target.value); e.target.value = ''; } }}>
                <option value="">Select exact batch…</option>
                {dispensable.map((m) => (
                  <option key={m.id} value={m.id}>
                    {isNearExpiry(m.expiry_date, nearExpiryDays) ? '⏳ ' : ''}{m.generic_name} {m.strength} — {m.batch_number} (exp {new Date(m.expiry_date).toLocaleDateString('en-IN')}, {m.quantity_available} left)
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}

          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2.5 border-b border-[#F3F4F6] py-2">
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold">
                  {item.name} {item.schedule !== 'OTC' && <ScheduleBadge schedule={item.schedule} />}
                  {item.prescription_item_id != null && !item.substitution_reason && (
                    <Badge variant="info" title="Linked to a prescribed line">Rx</Badge>
                  )}
                  {item.substitution_reason && (
                    <Badge variant="warn" title={`Substitution: ${item.substitution_reason}`}>🔀 Substituted</Badge>
                  )}
                  {item.quantity_override_reason && (
                    <Badge variant="warn" title={`Exceeds prescribed quantity: ${item.quantity_override_reason}`}>⚠ Over quantity</Badge>
                  )}
                  {item.off_prescription_reason && (
                    <Badge variant="warn" title={`Not on the prescription: ${item.off_prescription_reason}`}>⚠ Off-prescription</Badge>
                  )}
                  <NearExpiryBadge expiry={item.expiry} nearExpiryDays={nearExpiryDays} />
                  {item.batch_selection_reason && (
                    <Badge variant="warn" title={`Later-expiry batch: ${batchSelectionSummary(item, batchReasons)}`}>↷ Later batch</Badge>
                  )}
                </div>
                <div className="text-[11px] text-ink-muted">
                  Batch: {item.batch} · Exp: {item.expiry ? new Date(item.expiry).toLocaleDateString('en-IN') : '—'}
                  {item.substitution_reason && <> · Reason: {item.substitution_reason}</>}
                  {item.quantity_override_reason && <> · Reason: {item.quantity_override_reason}</>}
                  {item.off_prescription_reason && <> · Reason: {item.off_prescription_reason}</>}
                  {item.batch_selection_reason && <> · Batch reason: {batchSelectionSummary(item, batchReasons)}</>}
                </div>
              </div>
              <Input
                type="number"
                className="w-14"
                value={item.qty}
                min={1}
                max={item.available}
                onChange={(e) =>
                  setItems((prev) => prev.map((it, i) => (
                    // A quantity edit invalidates any quantity_override_reason
                    // already applied to this line — the amount it was given
                    // for may no longer be the amount actually being asked for,
                    // so it must be re-confirmed via the banner below.
                    i === idx ? { ...it, qty: Math.min(parseInt(e.target.value, 10) || 1, it.available), quantity_override_reason: undefined } : it
                  )))
                }
              />
              {gstEnabled && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-semibold text-ink-muted">GST</span>
                  <NativeSelect className="w-20 text-xs" value={item.gst} onChange={(e) => updateItemGst(idx, e.target.value)} title="GST % for this line">
                    {gstPercentageOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}%
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              )}
              <div className="min-w-[56px] text-right text-xs font-semibold">₹{(item.qty * item.mrp).toFixed(2)}</div>
              <Button variant="danger" size="icon" onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}>
                ✕
              </Button>
            </div>
          ))}

          {unresolvedMismatches.length > 0 && (
            <div className="mt-3 rounded-[var(--radius)] border border-danger-soft bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">
              <div className="font-bold uppercase tracking-wide text-[10px]">Blocked — does not match the prescription</div>
              {unresolvedMismatches.map((m) => (
                <div key={m.inventory_id} className="mt-1">
                  Prescribed <strong>{m.prescribed_drug}</strong>, but this line dispenses <strong>{m.dispensed_drug}</strong>.
                  Remove it or add a substitution reason above before generating the bill.
                </div>
              ))}
            </div>
          )}

          {unresolvedQuantityOverages.length > 0 && (
            <div className="mt-3 rounded-[var(--radius)] border border-danger-soft bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">
              <div className="font-bold uppercase tracking-wide text-[10px]">Blocked — exceeds the prescribed quantity</div>
              {unresolvedQuantityOverages.map((i) => {
                const remaining = i.prescription_item_id != null ? remainingByPrescriptionItemId.get(i.prescription_item_id) : null;
                return (
                  <div key={i.inventory_id} className="mt-1">
                    Dispensing <strong>{i.qty}</strong> of <strong>{i.name}</strong>, but only <strong>{remaining}</strong> remain on the prescription.
                  </div>
                );
              })}
              <div className="mt-2 flex gap-1.5">
                <Input
                  className="flex-1"
                  placeholder="Reason for exceeding the prescribed quantity (e.g. fixed strip size)"
                  value={quantityOverrideReason}
                  onChange={(e) => setQuantityOverrideReason(e.target.value)}
                />
                <Button size="sm" disabled={!quantityOverrideReason.trim()} onClick={applyQuantityOverrideReason}>Apply</Button>
              </div>
            </div>
          )}

          {unresolvedOffPrescription.length > 0 && (
            <div className="mt-3 rounded-[var(--radius)] border border-danger-soft bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">
              <div className="font-bold uppercase tracking-wide text-[10px]">Blocked — not on this patient’s prescription</div>
              {unresolvedOffPrescription.map((i) => (
                <div key={i.inventory_id} className="mt-1">
                  <strong>{i.name}</strong> was added outside the doctor’s prescribed list.
                  {i.schedule !== 'OTC' && <> This is a Schedule {i.schedule} medicine.</>} Remove it or add a reason before generating the bill.
                </div>
              ))}
              {offPrescriptionMayBeSubstitution && (
                <div className="mt-1.5 rounded-[var(--radius)] border border-warn-soft bg-warn-soft px-2 py-1.5 text-caution-ink">
                  ⚠ There's still an unfulfilled prescribed line below — if this is meant to replace it, remove this
                  line and use that line's own 🔀 Substitute button instead, so it's linked, counted against the
                  prescription, and tracked for reporting. Otherwise a reason here is fine.
                </div>
              )}
              <div className="mt-2 flex gap-1.5">
                <Input
                  className="flex-1"
                  placeholder="Reason for dispensing outside the prescription (e.g. doctor verbally approved, OTC add-on)"
                  value={offPrescriptionReason}
                  onChange={(e) => setOffPrescriptionReason(e.target.value)}
                />
                <Button size="sm" disabled={!offPrescriptionReason.trim()} onClick={applyOffPrescriptionReason}>Apply</Button>
              </div>
            </div>
          )}

          <LaterBatchPanel
            className="mt-3"
            issues={unresolvedLater}
            reasons={batchReasons}
            onApply={applyLaterBatchReason}
            onUseEarlier={switchToEarlierBatch}
          />

          <SafetyAlertBanner alerts={safetyAlerts} className="mt-3" />

          {items.length > 0 && (
            <div className="mt-3.5 rounded-[var(--radius)] bg-canvas p-3">
              {(gstEnabled ? ([['Subtotal', subtotal], ['GST', gstAmt], ['Total', total]] as const) : ([['Subtotal', subtotal], ['Total', total]] as const)).map(
                ([l, v], i, arr) => (
                  <div
                    key={l}
                    className={`flex justify-between ${i < arr.length - 1 ? 'mb-1.5' : ''} ${i === arr.length - 1 ? 'border-t border-line pt-2 text-base font-bold' : 'text-[13px]'}`}
                  >
                    {l}
                    <span>₹{v.toFixed(2)}</span>
                  </div>
                )
              )}

              {needsPrescriber && (
                <div className="mt-3 rounded-[var(--radius)] border border-warn-soft bg-warn-soft p-2.5">
                  <div className="mb-2 text-[11.5px] font-bold text-caution-ink">⚠ Schedule H1/X item(s) — statutory register requires:</div>
                  <div className="grid2">
                    <FormField label="Prescriber name" required>
                      <Input value={prescriberName} onChange={(e) => setPrescriberName(e.target.value)} />
                    </FormField>
                    <FormField label="Prescriber reg. no.">
                      <Input value={prescriberReg} onChange={(e) => setPrescriberReg(e.target.value)} />
                    </FormField>
                  </div>
                </div>
              )}

              <div className="grid2 mt-3">
                <FormField label="Payment mode">
                  <NativeSelect value={payMode} onChange={(e) => setPayMode(e.target.value as 'Cash' | 'UPI' | 'Card')}>
                    {(['Cash', 'UPI', 'Card'] as const).map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </NativeSelect>
                </FormField>
                <FormField label="Payment status">
                  <NativeSelect value={payStatus} onChange={(e) => setPayStatus(e.target.value as 'Paid' | 'Pending')}>
                    <option value="Paid">Paid now</option>
                    <option value="Pending">Pending</option>
                  </NativeSelect>
                </FormField>
              </div>
              <Button
                variant="success"
                full
                className="mt-3"
                disabled={
                  createBill.isPending ||
                  unresolvedMismatches.length > 0 ||
                  unresolvedQuantityOverages.length > 0 ||
                  unresolvedOffPrescription.length > 0 ||
                  unresolvedLater.length > 0
                }
                loading={createBill.isPending}
                onClick={genBill}
              >
                {createBill.isPending
                  ? 'Generating…'
                  : unresolvedMismatches.length > 0
                  ? 'Resolve mismatch to continue'
                  : unresolvedQuantityOverages.length > 0
                  ? 'Resolve quantity to continue'
                  : unresolvedOffPrescription.length > 0
                  ? 'Resolve off-prescription item to continue'
                  : unresolvedLater.length > 0
                  ? 'Resolve batch choice to continue'
                  : 'Generate pharmacy bill'}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
